<template>
    <div
        class="a-breadcrumbs"
        :class="className"
        role="navigation"
        aria-label="Breadcrumb"
    >
        <div class="a-container">
            <div class="a-breadcrumbs__desktop">
                <ul ref="listItem" class="a-breadcrumbs__list">
                    <li v-for="(item, index) in links" :key="index" class="a-breadcrumbs__item">
                        <a-link
                            v-if="bigWidth === true && item.to && index < links.length - 1"
                            v-bind="item"
                            class="a-breadcrumbs__link"
                            :to="item.to"
                            :text="item.text"
                            :size="item.size || 'caption'"
                            accent
                            :type="theme === 'dark' ? 'light' : 'regular'"
                        />
                        <span
                            v-if="item.to && index === links.length - 1"
                            class="a-breadcrumbs__current"
                            :class="item.size || 'caption'"
                            aria-current="page"
                        >
                            {{ item.text }}
                        </span>
                        <a-glyph
                            v-if="bigWidth === true && glyphDesktop && item.to && index < links.length - 1"
                            class="a-breadcrumbs__glyph"
                            :name="glyphDesktop"
                            :aria-hidden="true"
                        />
                        <a-link
                            v-if="bigWidth === false && item.to && (index === 0 || index === links.length - 2)"
                            v-bind="item"
                            class="a-breadcrumbs__link"
                            :to="item.to"
                            :text="item.text"
                            :size="item.size || 'caption'"
                            accent
                            :type="theme === 'dark' ? 'light' : 'regular'"
                        />
                        <a-glyph
                            v-if="bigWidth === false && glyphDesktop && item.to && (index === 0 || index === links.length - 2)"
                            class="a-breadcrumbs__glyph"
                            :name="glyphDesktop"
                            :aria-hidden="true"
                        />
                        <div
                            v-if="bigWidth === false && item.sublinks && hiddenLinks.length"
                            class="a-breadcrumbs__sublist-container"
                        >
                            <button
                                class="a-breadcrumbs__sublist-toggler"
                                :class="{ 'a-breadcrumbs__sublist-toggler--active': sublinkActive }"
                                type="button"
                                @click="showSublist"
                            >
                                <a-glyph class="a-breadcrumbs__glyph" name="elipsis" :aria-hidden="true" />
                            </button>
                            <ul ref="links" class="a-breadcrumbs__sublist" @mouseleave="hideSublist">
                                <li v-for="(sublink, subIndex) in hiddenLinks" :key="subIndex" class="a-breadcrumbs__subitem">
                                    <a-link
                                        class="a-breadcrumbs__sublink"
                                        v-bind="sublink"
                                        :to="sublink.to"
                                        :text="sublink.text"
                                        :size="sublink.size || 'caption'"
                                        accent
                                        :type="theme === 'dark' ? 'light' : 'regular'"
                                    />
                                </li>
                            </ul>
                            <a-glyph class="a-breadcrumbs__glyph" :name="glyphDesktop" :aria-hidden="true" />
                        </div>
                    </li>
                </ul>
            </div>
            <div class="a-breadcrumbs__mobile">
                <div v-for="(item, index) in links" :key="index" class="a-breadcrumbs__item">
                    <a-link
                        v-if="item.to && item.text && index === links.length - 2"
                        v-bind="item"
                        class="a-breadcrumbs__link"
                        :to="item.to"
                        :text="item.text"
                        :glyph="glyphMobile"
                        glyph-position="left"
                        :size="item.size || 'caption'"
                        accent
                        :type="theme === 'dark' ? 'light' : 'regular'"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AGlyph from '@core/components/glyph/glyph.vue';
import ALink from '@core/components/link/link.vue';

const sublistClassContainer = 'a-breadcrumbs__sublist-container';
const sublistClass = 'a-breadcrumbs__sublist-toggler';
const sublistClassActive = `${sublistClass}_active`;

export default {
    name: 'ABreadcrumbs',

    components: {
        ALink,
        AGlyph,
    },

    props: {
        /**
         * Links
         */
        links: {
            type: Array,
            default: () => [],
        },

        glyphDesktop: {
            type: String,
            required: false,
            default: 'breadcrumb-chevron-right',
        },

        glyphMobile: {
            type: String,
            required: false,
            default: 'long-arrow-left',
        },

        sublinks: {
            type: Boolean,
        },

        theme: {
            type: String,
            validator: (prop) => ['light', 'dark'].includes(prop),
            default: 'light',
        },
    },

    data() {
        return {
            sublinkActive: false,
            bigWidth: true,
        };
    },

    computed: {
        hiddenLinks() {
            return this.links.filter((link, index) => link.to && index > 0 && index < this.links.length - 2);
        },

        className() {
            return {
                'a-breadcrumbs_dark': this.theme === 'dark',
            };
        },
    },

    watch: {
        links: {
            handler: 'calcWidth',
            deep: true,
        },
    },

    created() {
        // Non-reactive bookkeeping for resize throttling and stale-measure protection.
        this.resizeFrame = null;
        this.calcId = 0;
    },

    mounted() {
        this.calcWidth();
        window.addEventListener('resize', this.onResize);
    },

    beforeUnmount() {
        window.removeEventListener('resize', this.onResize);
        cancelAnimationFrame(this.resizeFrame);
        this.calcId += 1;
    },

    methods: {
        showSublist(event) {
            if (event.target.classList.contains(sublistClassActive)) {
                event.target.classList.remove(sublistClassActive);
            } else {
                event.target.classList.add(sublistClassActive);
            }
        },

        hideSublist(event) {
            if (event.relatedTarget && event.relatedTarget.className !== sublistClassContainer) {
                event.target
                    .closest(`.${sublistClassContainer}`)
                    .querySelector(`.${sublistClass}`)
                    .classList.remove(sublistClassActive);
            }
        },

        onResize() {
            cancelAnimationFrame(this.resizeFrame);
            this.resizeFrame = requestAnimationFrame(this.calcWidth);
        },

        async calcWidth() {
            this.calcId += 1;
            const id = this.calcId;

            // Measure the complete trail, including links hidden in the collapsed state.
            this.bigWidth = true;
            await this.$nextTick();

            // A newer call (or unmount) has superseded this one.
            if (id !== this.calcId) {
                return false;
            }

            const list = this.$refs.listItem;
            if (!list || !list.clientWidth) {
                return false;
            }

            const contentWidth = Array.from(list.children)
                .reduce((width, item) => width + item.getBoundingClientRect().width, 0);
            const canCollapse = this.hiddenLinks.length > 0 && this.links.some((item) => item.sublinks);
            this.bigWidth = !canCollapse || contentWidth <= list.clientWidth;
            return true;
        },
    },
};
</script>

<style lang="postcss" scoped>
.a-breadcrumbs {
    width: 100%;
    position: relative;
    background: transparent;

    /* WEB-43924 why do we use 120 here ? */
    z-index: 120;
    &__desktop {
        display: none;
        @media (--viewport-tablet) {
            display: block;
        }
    }

    &__mobile {
        display: block;
        @media (--viewport-tablet) {
            display: none;
        }
    }
    &__current {
        color: var(--av-nav-primary);
        padding: 4px 8px;

        &.caption {
            @mixin caption-accent;
        }
        &.title {
            @mixin title-accent;
        }
    }
    &__list {
        display: flex;
        flex-shrink: 0;
        white-space: nowrap;
        flex-wrap: wrap;
    }

    &__item {
        @mixin caption-accent;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        color: var(--av-brand-secondary);
        .a-breadcrumbs__glyph {
            margin-inline-end: 4px;
        }
    }

    &__link {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 4px 8px;
        border-radius: 4px;
        @mixin caption-accent;
        &:hover {
            background-color: var(--el-secondary-hover);
        }
        &:active {
            background-color: var(--el-secondary-active);
        }

        :deep(.a-glyph) {
            margin: 0 !important;
        }
        :deep(.hasGlyph) {
            display: flex;
        }
    }

    &__glyph {
        fill: var(--av-link-primary);
        height: 16px;
        width: 16px;
    }

    &__sublist-container {
        position: relative;
        display: flex;
        align-items: center;
    }

    &__sublist-toggler {
        display: flex;
        align-items: center;
        padding: 4px 8px;
        color: var(--av-brand-secondary);
        background: transparent;
        margin-inline-end: 4px;
        border-radius: 4px;
        border: 1px solid var(--av-brand-secondary-light);
        cursor: pointer;

        .a-breadcrumbs__glyph {
            fill: var(--av-brand-primary) !important;
            margin: 0;
        }

        & > * {
            pointer-events: none;
        }

        &:hover {
            background-color: var(--el-secondary-hover);
        }

        &_active {
            background-color: var(--el-secondary-active);
            & + .a-breadcrumbs {
                &__sublist {
                    display: block;
                }
            }
        }

        :deep(.a-link.nuxt-link-exact-active) {
            color: var(--av-nav-primary);
        }

        &--active {
            color: var(--av-inversed-primary);
        }
    }

    &__sublist-glyph {
        margin-inline-start: 8px;
        fill: currentcolor;
        transition: transform 0.15s ease-out;
    }

    &__sublist {
        display: none;
        position: absolute;
        top: calc(100% + 10px);
        inset-inline-start: 0;
        padding: 8px 0;
        background-color: var(--av-inversed-primary);
        box-shadow: 0 10px 20px rgba(36, 49, 67, 0.2);
        border-radius: 4px;
        border: 1px solid var(--av-brand-secondary);
    }

    &__subitem {
        margin: 0;
        padding: 0;
    }

    &__sublink {
        cursor: pointer;
        display: block;
        padding: 8px 16px;
        :deep(.a-glyph) {
            margin: 0 !important;
        }
        &:hover {
            background-color: var(--el-secondary-hover);
        }

        &:active {
            background-color: var(--el-secondary-active);
        }

        &.a-link.nuxt-link-exact-active {
            pointer-events: auto;
        }
    }
    &_dark {
        background: var(--av-nav-primary);
    }
    &_dark & {
        &__current {
            color: var(--av-inversed-primary);
        }
        &__sublist-toggler {
            color: var(--av-link-inversed);
            .a-breadcrumbs__glyph {
                fill: var(--av-link-inversed) !important;
            }
        }
        &__link {
            color: var(--av-link-inversed) !important;
            :deep(.a-glyph) {
                fill: var(--av-link-inversed) !important;
            }
        }
        &__glyph {
            fill: var(--av-link-inversed);
        }
    }
}
[dir='rtl'] {
    .a-breadcrumbs__item .a-breadcrumbs__glyph {
        transform: rotate(180deg);
    }
}
</style>
